/**
 * Client for the Wayfarer auth/sync backend (server/ in this repo, mounted at
 * <base>/api). Cookie-based sessions: the browser carries the httpOnly session
 * cookie, nothing token-like is stored in JS-visible storage.
 */

export interface User {
  id: number;
  name: string;
  role: 'admin' | 'driver';
  status: 'pending' | 'approved' | 'disabled';
}

export interface PendingUser {
  id: number;
  name: string;
  createdAt: string;
  status: User['status'];
  role: User['role'];
}

const API = `${import.meta.env.BASE_URL}api`;

class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API}${path}`, {
      ...init,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...init.headers },
      // A hung request must fail visibly, never stall the UI forever - flaky
      // car connectivity and broken HTTP/3 paths both present as hangs.
      signal: init.signal ?? AbortSignal.timeout(12_000),
    });
  } catch (err) {
    if (err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError')) {
      throw new ApiError('Server not responding - check your connection and try again.', 0);
    }
    throw new ApiError('Network error - are you online?', 0);
  }
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    /* empty body is fine for some endpoints */
  }
  if (!res.ok) {
    throw new ApiError(body?.error ?? `Request failed (${res.status})`, res.status);
  }
  return body as T;
}

/* ------------------------------ session ---------------------------------- */

export function me(): Promise<{ user: User | null }> {
  return call('/me');
}

export function signup(name: string, pin: string): Promise<{ user: User }> {
  return call('/signup', { method: 'POST', body: JSON.stringify({ name, pin }) });
}

export function login(name: string, pin: string): Promise<{ user: User }> {
  return call('/login', { method: 'POST', body: JSON.stringify({ name, pin }) });
}

export function logout(): Promise<void> {
  return call('/logout', { method: 'POST' });
}

/* ------------------------------- admin ------------------------------------ */

export function adminListUsers(): Promise<{ users: PendingUser[] }> {
  return call('/admin/users');
}

export function adminSetStatus(
  userId: number,
  status: 'approved' | 'disabled',
): Promise<{ ok: true }> {
  return call(`/admin/users/${userId}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

export function adminResetPin(userId: number, pin: string): Promise<{ ok: true }> {
  return call(`/admin/users/${userId}/pin`, { method: 'POST', body: JSON.stringify({ pin }) });
}

/* ------------------------------- trips ------------------------------------ */

import type { Trip } from '../core/types';

/** Push a finished trip to the server (also kept in local IndexedDB). */
export function syncTrip(trip: Trip): Promise<{ ok: true }> {
  return call('/trips', { method: 'POST', body: JSON.stringify(trip) });
}

export function fetchTrips(): Promise<{ trips: Trip[] }> {
  return call('/trips');
}

/* --------------------------- shared saved places -------------------------- */

export type PlaceCategory = 'home' | 'work' | 'showroom' | 'depot' | 'other';

export interface SavedPlace {
  id: string;
  name: string;
  lng: number;
  lat: number;
  kind: 'place' | 'shared-location';
  category?: PlaceCategory;
  createdBy: number;
  creatorName: string;
  createdAt: string;
}

export function fetchPlaces(): Promise<{ places: SavedPlace[] }> {
  return call('/places');
}

export function createPlace(
  name: string,
  lng: number,
  lat: number,
  kind: 'place' | 'shared-location' = 'place',
  category: PlaceCategory = 'other',
): Promise<{ place: SavedPlace }> {
  return call('/places', {
    method: 'POST',
    body: JSON.stringify({ name, lng, lat, kind, category }),
  });
}

export function deletePlace(id: string): Promise<{ ok: true }> {
  return call(`/places/${id}`, { method: 'DELETE' });
}

export { ApiError };
